import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Link, useRouteError } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

function Fallback({ detalhe }: { detalhe?: string }) {
  const { t } = useTranslation('app');
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 p-8 text-center">
      <h1 className="font-display text-2xl font-bold">{t('erro.titulo')}</h1>
      <p className="max-w-md text-sm text-vyxora-text-light/70">
        {t('erro.descricao')}
      </p>
      {detalhe && (
        <pre className="max-w-full overflow-x-auto rounded border border-white/10 bg-black/30 p-3 text-left text-xs text-vyxora-text-light/60">
          {detalhe}
        </pre>
      )}
      <div className="flex gap-3">
        <button className="btn-primary" onClick={() => window.location.reload()}>
          {t('erro.recarregar')}
        </button>
        <Link className="btn-secondary" to="/app">
          {t('erro.irParaInicio')}
        </Link>
      </div>
    </div>
  );
}

/**
 * `errorElement` do React Router: pega erros de loader/render das rotas filhas.
 * Sem isto, qualquer throw derruba a árvore inteira e o usuário vê tela branca.
 */
export function RouteErrorElement() {
  const error = useRouteError();
  const detalhe = error instanceof Error ? error.message : undefined;
  return <Fallback detalhe={detalhe} />;
}

type Props = { children: ReactNode };
type State = { erro: Error | null };

/** Boundary de classe para envolver subárvores fora do alcance do router. */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { erro: null };

  static getDerivedStateFromError(erro: Error): State {
    return { erro };
  }

  componentDidCatch(erro: Error, info: ErrorInfo) {
    console.error('ErrorBoundary capturou:', erro, info.componentStack);
  }

  render() {
    if (this.state.erro) return <Fallback detalhe={this.state.erro.message} />;
    return this.props.children;
  }
}
